import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockRadioGroup from '../components/GuideMockRadioGroup';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuidePartnerUpdate: React.FC = () => {
  return (
    <GuidePageLayout
      title="パートナー情報を更新する"
      description="既存パートナーの情報を変更します（住所変更、契約終了等）。"
      breadcrumbs={[{ label: 'パートナー管理', path: '/guide/partner/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['対象パートナー', '存在すること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="画面モード">
        <GuideTable
          headers={['モード', 'タイトル', '説明']}
          rows={[
            [
              '参照モード',
              'パートナー詳細',
              'パートナー情報を参照専用で表示。編集ボタンで編集モードに切替',
            ],
            [
              '編集モード',
              'パートナー詳細（編集中）',
              '各フィールドが入力可能になる。戻る矢印で参照モードに戻る',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: 'パートナー管理画面を開く' },
            { label: 'パートナー一覧から対象パートナーを検索・選択する' },
            {
              label: '参照モード（パートナー詳細）が表示される',
              description:
                'ヘッダー右上にパートナーIDが表示されます。コピーボタンでクリップボードにコピーできます。',
              content: (
                <GuideMockContainer label="参照モード（ヘッダー部分）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        パートナー詳細
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「編集」ボタンを押下して編集モードに切り替える',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="編集" startIcon={<EditIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '編集フォームで変更したい項目を修正する',
              content: (
                <GuideMockContainer label="編集モード">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        パートナー詳細（編集中）
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockTextField
                          label="パートナー名"
                          required
                          value="サンプル株式会社"
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField
                          label="パートナー名（カナ）"
                          required
                          value="サンプルカブシキガイシャ"
                        />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockRadioGroup
                          label="パートナー種別"
                          options={['法人', '個人事業主']}
                          required
                          value="法人"
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="契約開始日（年度月選択）" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="契約終了日（年度月選択）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「更新」ボタンを押下する',
              content: (
                <GuideMockContainer label="編集モード フッター">
                  <GuideMockButton label="更新" startIcon={<SaveIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', 'モード', '動作']}
          rows={[
            ['← 戻る矢印（参照モード）', '参照', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '共通', 'パートナーIDをクリップボードにコピー'],
            ['編集ボタン', '参照', '編集モードに切り替える'],
            ['← 戻る矢印（編集モード）', '編集', '編集内容を破棄して参照モードに戻る'],
            ['更新ボタン', '編集', '入力内容でパートナー情報を更新する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox type="warning">
            契約終了日を設定し、その日時を過ぎるとパートナーは「契約終了」状態となり、一覧のデフォルト検索で非表示になります。
          </GuideInfoBox>
          <GuideInfoBox>契約終了日を空にすると「契約中」に復帰します。</GuideInfoBox>
          <GuideInfoBox>
            編集モードで戻る矢印をクリックすると、未保存の変更は破棄されます。
          </GuideInfoBox>
          <GuideInfoBox>
            契約開始日・契約終了日は年度月選択（FinancialYearMonthSelect）から選択します。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuidePartnerUpdate;
